import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Basic E.164 validation — must start with +, 8–15 digits total. */
export function isValidE164(phone: string): boolean {
  return /^\+[1-9]\d{7,14}$/.test(phone);
}

/** Parse a textarea of phone numbers (one per line or comma-separated) into an E.164 list. */
export function parsePhoneList(raw: string): { valid: string[]; invalid: string[] } {
  const tokens = raw
    .split(/[\n,]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const t of tokens) {
    if (isValidE164(t)) valid.push(t);
    else invalid.push(t);
  }
  return { valid, invalid };
}

/** Format bytes as human-readable string. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Detect the browser's IANA timezone. */
export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Build an MCP connection URL (per-org MCP URLs plan §3).
 *  - No args → the legacy workspace-wide endpoint (`/mcp`): the credential
 *    picks the workspace. Unchanged shape so every existing caller that
 *    passes no opts keeps returning exactly what it always has (plan AC1).
 *  - `{ slug }` → the org-scoped endpoint (`/mcp/{slug}`): the URL picks the
 *    workspace instead of the credential.
 *  - `{ slug, family }` → a source-scoped endpoint (`/mcp/{slug}/{family}`),
 *    e.g. family `whatsapp` | `slack` | `signal` | `granola`.
 */
export function mcpEndpoint(opts?: { slug?: string; family?: string }): string {
  const origin = window.location.origin;
  if (!opts?.slug) return `${origin}/mcp`;
  return opts.family ? `${origin}/mcp/${opts.slug}/${opts.family}` : `${origin}/mcp/${opts.slug}`;
}
